(() => {
  if (customElements.get('recently-viewed-products')) return;

  const storageKey = 'deqalli:recently-viewed:v1';
  const maxAge = 30 * 24 * 60 * 60 * 1000;
  // The section supports 25 cards; leave room for the current product as well.
  const historyLimit = 26;
  const changeEvent = 'recently-viewed:change';
  const currentProduct = document.querySelector('[data-recently-viewed-product]')?.dataset;
  let allowed = false;

  function history() {
    if (!allowed) return [];
    try {
      const entries = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (!Array.isArray(entries)) return [];
      const now = Date.now();
      const seen = new Set();
      return entries
        .filter((entry) => entry && /^\d+$/.test(entry.id) && typeof entry.handle === 'string' &&
          entry.handle.length > 0 && entry.handle.length <= 255 && !/[/?#]/.test(entry.handle) &&
          Number.isFinite(entry.viewedAt) && entry.viewedAt > now - maxAge && entry.viewedAt <= now)
        .sort((a, b) => b.viewedAt - a.viewedAt)
        .filter((entry) => {
          if (seen.has(String(entry.id))) return false;
          seen.add(String(entry.id));
          return true;
        })
        .slice(0, historyLimit);
    } catch {
      return [];
    }
  }

  function rememberProduct() {
    if (!allowed || !currentProduct?.productId || !currentProduct.productHandle) return;
    const context = currentProduct.productContext || document.documentElement.dataset.storefrontContext || '';
    const entry = { id: currentProduct.productId, handle: currentProduct.productHandle, viewedAt: Date.now(), context };
    try {
      localStorage.setItem(storageKey, JSON.stringify([
        entry, ...history().filter((item) => String(item.id) !== entry.id),
      ].slice(0, historyLimit)));
    } catch {
      // Browser storage may be disabled or full. The storefront remains usable.
    }
  }

  function syncConsent() {
    try {
      allowed = window.Shopify?.customerPrivacy?.preferencesProcessingAllowed() === true;
    } catch {
      allowed = false;
    }
    if (allowed) {
      rememberProduct();
    } else {
      try { localStorage.removeItem(storageKey); } catch { /* Storage is optional. */ }
    }
  }

  const ready = new Promise((resolve) => {
    if (window.Shopify?.designMode || window.Shopify?.customerPrivacy) return resolve();
    if (!window.Shopify?.loadFeatures) return resolve();
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], resolve);
  }).then(() => {
    if (!window.Shopify?.designMode) syncConsent();
  });

  window.DeqalliRecentlyViewed = { ready, getEntries: history, isAllowed: () => allowed };

  if (!window.Shopify?.designMode) {
    document.addEventListener('visitorConsentCollected', () => {
      ready.then(() => {
        syncConsent();
        document.dispatchEvent(new Event(changeEvent));
      });
    });
    window.addEventListener('storage', (event) => {
      if (event.key === storageKey || event.key === null) document.dispatchEvent(new Event(changeEvent));
    });
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      ready.then(() => {
        syncConsent();
        document.dispatchEvent(new Event(changeEvent));
      });
    });
  }

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      if (window.Shopify?.designMode) return;
      this.onHistoryChange = this.refresh.bind(this);
      document.addEventListener(changeEvent, this.onHistoryChange);
      ready.then(() => { if (this.isConnected) this.refresh(); });
    }

    disconnectedCallback() {
      document.removeEventListener(changeEvent, this.onHistoryChange);
      this.observer?.disconnect();
      this.abortController?.abort();
    }

    refresh() {
      this.observer?.disconnect();
      this.abortController?.abort();
      this.abortController = null;
      const limit = Math.max(2, Math.min(25, Number(this.dataset.limit) || 4));
      const entries = history()
        .filter((entry) => String(entry.id) !== this.dataset.currentProductId)
        .slice(0, limit);
      this.hidden = entries.length === 0;
      if (this.hidden) {
        this.querySelector('.collection')?.remove();
        this.removeAttribute('aria-busy');
        return;
      }

      if (!this.querySelector('.collection')) {
        const layout = this.querySelector('[data-recently-viewed-layout]').content.cloneNode(true);
        const items = layout.querySelectorAll('.product-grid > li');
        items.forEach((item, index) => { if (index >= entries.length) item.remove(); });
        this.configureSlider(layout, entries.length);
        layout.querySelector('.product-grid').setAttribute('aria-hidden', 'true');
        layout.querySelector('.collection').setAttribute('inert', '');
        this.appendChild(layout);
      }
      this.setAttribute('aria-busy', 'true');

      if (!('IntersectionObserver' in window)) return this.loadCards(entries);
      this.observer = new IntersectionObserver((records) => {
        if (!records.some((record) => record.isIntersecting)) return;
        this.observer.disconnect();
        this.loadCards(entries);
      }, { rootMargin: '1600px 0px' });
      this.observer.observe(this);
    }

    configureSlider(root, count) {
      const mobile = this.dataset.swipeOnMobile === 'true' && count > Number(this.dataset.columnsMobile);
      const desktop = this.dataset.desktopSlider === 'true' && count > Number(this.dataset.columnsDesktop);
      const fullWidth = this.dataset.fullWidth === 'true';
      const pagination = this.dataset.showPagination === 'true';
      const slider = root.querySelector('slider-component');
      const list = root.querySelector('.product-grid');
      const title = root.querySelector('.collection__title');

      title.classList.toggle('title-wrapper--self-padded-tablet-down', mobile);
      title.classList.toggle('collection__title--desktop-slider', desktop);
      slider.classList.toggle('page-width', !mobile);
      slider.classList.toggle('page-width-desktop', !desktop && !fullWidth);
      slider.classList.toggle('slider-component-desktop', desktop);
      list.classList.toggle('slider', mobile || desktop);
      list.classList.toggle('slider--tablet', mobile);
      list.classList.toggle('grid--peek', mobile);
      list.classList.toggle('slider--desktop', desktop);
      if ((mobile || desktop) && !pagination) list.setAttribute('tabindex', '0');
      else list.removeAttribute('tabindex');
      list.querySelectorAll(':scope > li').forEach((item) => item.classList.toggle('slider__slide', mobile || desktop));
      root.querySelector('.slider-buttons').classList.toggle('hidden', !pagination || !(mobile || desktop));
    }

    async loadCards(entries) {
      const controller = new AbortController();
      this.abortController = controller;
      const timeout = setTimeout(() => controller.abort(), 10000);
      const cards = [];
      try {
        // Bound concurrency, preserve visit order, and skip failed product requests.
        for (let index = 0; index < entries.length && !controller.signal.aborted; index += 4) {
          const batch = await Promise.all(entries.slice(index, index + 4).map((entry) => this.fetchCard(entry, controller.signal)));
          cards.push(...batch.filter(Boolean));
        }
      } finally {
        clearTimeout(timeout);
      }
      if (!this.isConnected || this.abortController !== controller || !allowed) return;
      if (!cards.length) {
        this.removeAttribute('aria-busy');
        if (this.querySelector('.collection')?.hasAttribute('inert')) {
          this.querySelector('.collection').remove();
          this.hidden = true;
        }
        return;
      }

      const list = this.querySelector('.product-grid');
      const fragment = document.createDocumentFragment();
      cards.forEach((card, index) => {
        const item = document.createElement('li');
        item.id = `Slide-${this.dataset.sectionId}-${index + 1}`;
        item.className = 'grid__item';
        item.appendChild(card);
        fragment.appendChild(item);
      });
      const visibleCount = window.matchMedia('(min-width: 990px)').matches
        ? Number(this.dataset.columnsDesktop) || 6 : (Number(this.dataset.columnsMobile) || 2) + 1;
      const prepared = await prepareProductCardImages(fragment, visibleCount, controller.signal);
      if (!this.isConnected || this.abortController !== controller || !allowed) return;
      this.removeAttribute('aria-busy');
      if (!prepared) {
        if (this.querySelector('.collection')?.hasAttribute('inert')) this.hidden = true;
        return;
      }
      list.replaceChildren(fragment);
      list.removeAttribute('aria-hidden');
      this.querySelector('.collection').removeAttribute('inert');
      this.configureSlider(this, cards.length);
      this.querySelector('slider-component').resetPages?.();
      if (this.dataset.animate === 'true') {
        this.querySelectorAll('.title, .collection__description, slider-component, .product-grid > li').forEach((element) => {
          element.classList.add('scroll-trigger', 'animate--slide-in');
        });
        window.initializeScrollAnimationTrigger?.(this);
      }
    }

    async fetchCard(entry, signal) {
      try {
        const url = new URL(`${window.Shopify.routes.root}products/${encodeURIComponent(entry.handle)}`, location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url, { signal });
        if (!response.ok) return null;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const template = html.querySelector('template[data-recently-viewed-card]');
        if (template?.dataset.productId !== String(entry.id)) return null;
        const card = document.importNode(template.content, true);
        card.querySelectorAll('quick-add-bulk').forEach((element) => {
          element.dataset.productUrl = template.dataset.productUrl;
        });
        return card;
      } catch {
        return null;
      }
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
})();
